import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Lock, ArrowRight, Send, Image as ImageIcon, X, Eye } from 'lucide-react';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || '';

const PRIORITY_LABELS = { low: 'پایین', medium: 'متوسط', high: 'بالا' };
const PRIORITY_COLORS = {
  low: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  medium: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  high: 'bg-red-500/20 text-red-300 border-red-500/30'
};

const STATUS_LABELS = {
  open: 'باز',
  in_progress: 'در حال بررسی',
  waiting_user: 'پاسخ پشتیبانی',
  waiting_admin: 'در انتظار پشتیبانی',
  resolved: 'حل شده',
  closed: 'بسته شده'
};

const STATUS_COLORS = {
  open: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  in_progress: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  waiting_user: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  waiting_admin: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  resolved: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const DEPT_LABELS = { management: 'مدیریت', technical: 'فنی و خرید' };

const formatMessageTime = (dateStr) => {
  try {
    if (!dateStr) return '';
    const normalized = dateStr.includes('T') || dateStr.includes('Z') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
    const d = new Date(normalized);
    return new Intl.DateTimeFormat('fa-IR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Tehran'
    }).format(d);
  } catch {
    return '';
  }
};

const getMessageDateDay = (dateStr) => {
  try {
    if (!dateStr) return '';
    const normalized = dateStr.includes('T') || dateStr.includes('Z') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
    const d = new Date(normalized);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Tehran'
    }).format(d);
  } catch {
    return '';
  }
};

export default function TicketDetail() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [activePreviewImage, setActivePreviewImage] = useState(null);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
  }, [token, navigate]);

  const fetchTicket = useCallback(async (isSilent = false) => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/tickets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 403 || res.status === 404) {
        if (!isSilent) toast.error('تیکت یافت نشد');
        navigate('/tickets');
        return;
      }
      const data = await res.json();
      setTicket(data.ticket);
      setMessages(data.messages || []);
    } catch {
      if (!isSilent) toast.error('خطا در بارگذاری تیکت');
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [token, id, navigate]);

  useEffect(() => {
    fetchTicket(false);
    const interval = setInterval(() => {
      fetchTicket(true);
    }, 3000);
    return () => clearInterval(interval);
  }, [fetchTicket]);

  const chatContainerRef = useRef(null);
  const prevMsgCountRef = useRef(0);

  useEffect(() => {
    if (messages.length > prevMsgCountRef.current && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      prevMsgCountRef.current = messages.length;
    }
  }, [messages.length]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم تصویر نباید بیشتر از ۵ مگابایت باشد');
      return;
    }
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedImage) return;
    if (ticket?.status === 'closed') {
      toast.error('تیکت بسته شده است');
      return;
    }
    setSending(true);

    try {
      const formData = new FormData();
      if (newMessage.trim()) formData.append('message', newMessage.trim());
      if (selectedImage) formData.append('image', selectedImage);

      const res = await fetch(`${API}/api/tickets/${id}/messages`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setNewMessage('');
      removeSelectedImage();
      await fetchTicket(true);
    } catch (err) {
      toast.error(err.message || 'خطا در ارسال پیام');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm('آیا مطمئنید؟ بعد از بستن تیکت امکان ارسال پیام وجود ندارد.')) return;
    setClosing(true);
    try {
      const res = await fetch(`${API}/api/tickets/${id}/close`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      toast.success('تیکت بسته شد');
      await fetchTicket(true);
    } catch (err) {
      toast.error(err.message || 'خطا در بستن تیکت');
    } finally {
      setClosing(false);
    }
  };

  const formatDate = (date) => {
    try {
      if (!date) return '';
      let dateObj;
      if (typeof date === 'string') {
        const normalized = date.includes('T') || date.includes('Z') ? date : date.replace(' ', 'T') + 'Z';
        dateObj = new Date(normalized);
      } else {
        dateObj = new Date(date);
      }
      if (isNaN(dateObj.getTime())) return '';
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Tehran'
      }).format(dateObj);
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050811] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!ticket) return null;

  const isClosed = ticket.status === 'closed' || ticket.status === 'resolved';

  return (
    <div className="min-h-screen bg-[#050811] pt-20 pb-6 text-white" dir="rtl">
      <div className="max-w-3xl mx-auto px-4">
        {/* Back */}
        <button
          onClick={() => navigate('/tickets')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-5 text-sm"
        >
          <ArrowRight className="w-4 h-4" />
          <span>بازگشت به تیکت‌ها</span>
        </button>

        {/* Ticket Header Card */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-4"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-gray-500 text-sm">#{ticket.id}</span>
                <span className="bg-purple-500/20 text-purple-300 border border-purple-500/30 text-xs px-2.5 py-0.5 rounded-full">
                  {DEPT_LABELS[ticket.department] || ticket.department}
                </span>
                <span className={`text-xs px-2.5 py-0.5 rounded-full border ${PRIORITY_COLORS[ticket.priority]}`}>
                  {PRIORITY_LABELS[ticket.priority]}
                </span>
              </div>
              <h1 className="text-xl font-bold text-white">{ticket.subject}</h1>
              <p className="text-xs text-gray-500 mt-1">ثبت شده: {formatDate(ticket.createdAt)}</p>
            </div>

            <div className="flex items-center gap-3">
              <span className={`px-3 py-1.5 rounded-xl text-sm border font-medium ${STATUS_COLORS[ticket.status] || STATUS_COLORS.open}`}>
                {STATUS_LABELS[ticket.status] || ticket.status}
              </span>
              {!isClosed && ticket.userId === user?.id && (
                <button
                  onClick={handleClose}
                  disabled={closing}
                  className="px-3 py-1.5 bg-red-500/20 border border-red-500/30 text-red-400 rounded-xl text-sm hover:bg-red-500/30 transition-all disabled:opacity-50"
                >
                  {closing ? 'در حال بستن...' : 'بستن تیکت'}
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Messages */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          {/* Chat Area */}
          <div ref={chatContainerRef} className="p-4 space-y-4 max-h-[calc(100vh-400px)] min-h-[300px] overflow-y-auto">
            {messages.map((msg, i) => {
              const isAdmin = msg.senderType === 'admin';
              const currentDateDay = getMessageDateDay(msg.createdAt);
              const prevDateDay = i > 0 ? getMessageDateDay(messages[i - 1].createdAt) : null;
              const showDateHeader = i === 0 || currentDateDay !== prevDateDay;

              return (
                <React.Fragment key={msg.id}>
                  {showDateHeader && (
                    <div className="flex justify-center my-3">
                      <span className="bg-white/10 text-gray-300 text-xs px-3 py-1 rounded-full backdrop-blur-md border border-white/10 font-medium">
                        {currentDateDay}
                      </span>
                    </div>
                  )}
                  <motion.div
                    initial={{ opacity: 0, x: isAdmin ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.05, 0.3) }}
                    className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-[80%] ${isAdmin ? 'items-start' : 'items-end'} flex flex-col gap-1`}>
                      {/* Sender Name */}
                      <div className={`text-xs font-medium flex items-center gap-1.5 ${isAdmin ? 'text-emerald-400' : 'text-cyan-400'}`}>
                        {isAdmin && <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>}
                        {msg.senderName || (isAdmin ? 'ادمین' : 'شما')}
                        {isAdmin && <span className="text-gray-500 font-normal">(پشتیبانی)</span>}
                        {!isAdmin && <span className="inline-block w-2 h-2 rounded-full bg-cyan-400"></span>}
                      </div>

                      {/* Message Bubble */}
                      <div
                        className={`rounded-2xl p-3 text-sm leading-relaxed space-y-2 break-words [overflow-wrap:anywhere] max-w-full ${
                          isAdmin
                            ? 'bg-emerald-500/15 border border-emerald-500/20 text-white rounded-tr-2xl rounded-tl-sm'
                            : 'bg-cyan-500/15 border border-cyan-500/20 text-white rounded-tl-2xl rounded-tr-sm'
                        }`}
                      >
                        {msg.message && <p className="break-words [overflow-wrap:anywhere] whitespace-pre-wrap">{msg.message}</p>}

                        {/* Image Attachment */}
                        {msg.image && (
                          <div className="relative group cursor-pointer overflow-hidden rounded-xl border border-white/20">
                            <img
                              src={`${API}${msg.image}`}
                              alt="پیوست"
                              className="max-w-xs max-h-60 object-cover rounded-xl group-hover:scale-105 transition-transform"
                              onClick={() => setActivePreviewImage(`${API}${msg.image}`)}
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity" onClick={() => setActivePreviewImage(`${API}${msg.image}`)}>
                              <Eye className="w-6 h-6 text-white" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Time */}
                      <span className="text-[11px] text-gray-500 font-mono px-1">{formatMessageTime(msg.createdAt)}</span>
                    </div>
                  </motion.div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Image Preview Before Send */}
          {imagePreview && (
            <div className="px-4 pt-3 border-t border-white/10 flex items-center gap-3 bg-white/5">
              <div className="relative w-16 h-16 rounded-xl border border-emerald-500/40 overflow-hidden">
                <img src={imagePreview} alt="پیش‌نمایش" className="w-full h-full object-cover" />
                <button
                  onClick={removeSelectedImage}
                  className="absolute top-0.5 right-0.5 bg-black/80 text-white rounded-full p-0.5 hover:bg-red-500 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <span className="text-xs text-emerald-400 font-medium">تصویر انتخاب شد (آماده ارسال)</span>
            </div>
          )}

          {/* Input Area */}
          {isClosed ? (
            <div className="border-t border-white/10 p-4 text-center text-gray-500 text-sm bg-white/3 flex items-center justify-center gap-2">
              <Lock className="w-4 h-4" />
              <span>این تیکت بسته شده است</span>
            </div>
          ) : (
            <form
              onSubmit={handleSendMessage}
              className="border-t border-white/10 p-4 flex items-center gap-3"
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2.5 bg-white/5 border border-white/10 hover:border-emerald-500/50 rounded-xl text-gray-400 hover:text-emerald-400 transition-all shrink-0"
                title="آپلود تصویر"
              >
                <ImageIcon className="w-5 h-5" />
              </button>

              <textarea
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(e); }
                }}
                placeholder="پیام خود را بنویسید... (Enter برای ارسال)"
                rows={2}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors resize-none text-sm"
              />

              <button
                type="submit"
                disabled={sending || (!newMessage.trim() && !selectedImage)}
                className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-40 self-end flex items-center gap-1.5 shrink-0"
              >
                <Send className="w-4 h-4" />
                <span>{sending ? '...' : 'ارسال'}</span>
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Full Image Zoom Modal */}
      <AnimatePresence>
        {activePreviewImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={() => setActivePreviewImage(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setActivePreviewImage(null)}
                className="absolute top-3 right-3 z-10 p-2 bg-black/70 text-white rounded-full hover:bg-red-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <img src={activePreviewImage} alt="تصویر تیکت" className="max-w-full max-h-[85vh] object-contain rounded-2xl" />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
